import React from 'react';
import { FiActivity, FiLoader, FiTrendingDown } from 'react-icons/fi';

import { PeriodPicker } from '@/shared/components/ui';

import { useAgentInsights } from '../../hooks/useAgentAnalytics';
import { useAnalyticsPeriod } from '../../hooks/useAnalyticsPeriod';
import {
  useAcknowledgeDriftAlert,
  useAgentDriftAlerts,
} from '../../hooks/useAgentHealthMonitor';
import { useLatestSnapshot } from '../../hooks/useAgentTraining';
import type { AgentDriftAlertResponse } from '../../services/agentHealthMonitorApi';
import type { SnapshotRegressionInfo } from '../../services/agentTrainingApi';
import { ANALYTICS_PERIOD_SEGMENTS } from '../../utils/analytics-period';
import { getMetricDescription, getMetricLabel } from '../../utils/metric-labels';

import { HealthRecommendationsPanel } from './HealthRecommendationsPanel';
import { HealthScoreHero } from './HealthScoreHero';
import { HealthTimelineChart } from './HealthTimelineChart';
import { InsightCard } from './InsightCard';

interface HealthTabProps {
  agentId: string;
  /** Abre o modal de treinamento (Sprint UI-2) */
  onTrainAgent: () => void;
  /** Navega pra tab "Lacunas" (Sprint UI-3) */
  onViewGaps: () => void;
}

/**
 * Tab "Saúde" dentro do AgentFormPage. Mostra:
 * 1. Score gigante + interpretação (HealthScoreHero)
 * 2. Três cards narrativos gerados por LLM (InsightCard x3)
 * 3. Gráfico temporal de evolução (HealthTimelineChart, usa período 30d fixo
 *    para ter janela interessante independente do filtro dos outros componentes)
 * 4. Painel de recomendações com CTAs pra próximas sprints (UI-2 e UI-3)
 *
 * O filtro de período (1d/7d/30d/90d ou Personalizado) afeta score hero,
 * insights e os próximos passos. O gráfico de evolução sempre usa 30d pra
 * mostrar contexto mais amplo.
 */
export const HealthTab: React.FC<HealthTabProps> = ({
  agentId,
  onTrainAgent,
  onViewGaps,
}) => {
  const { value: periodValue, onChange: setPeriodValue, period, range } = useAnalyticsPeriod();
  const { data: insights, isLoading: loadingInsights } = useAgentInsights(
    agentId,
    period,
    range,
  );
  // Coach v2: busca o último snapshot pra exibir banner de regressão quando
  // o treino mais recente detectou piora em relação ao anterior.
  const { data: latestSnapshot } = useLatestSnapshot(agentId);
  const regressionInfo = latestSnapshot?.regressionInfo ?? null;

  // Opção B: alertas de drift de PRODUÇÃO (gerados pelo cron diário
  // comparando scores de Langfuse entre períodos de 7 dias).
  const { data: driftAlertsData } = useAgentDriftAlerts(agentId);
  const activeDriftAlert = driftAlertsData?.items[0] ?? null;

  return (
    <div className="space-y-6">
      {/* Header com filtro de período */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Saúde do agente
          </h2>
          <p
            className="text-sm mt-1"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Diagnóstico do que o agente está fazendo bem e onde pode melhorar.
          </p>
        </div>
        <PeriodPicker
          value={periodValue}
          onChange={setPeriodValue}
          segments={ANALYTICS_PERIOD_SEGMENTS}
          ariaLabel="Período da saúde do agente"
          className="flex-wrap"
          data-testid="agent-health-period"
        />
      </div>

      {/* Opção B: Alerta de drift de PRODUÇÃO — detectado pelo cron
          diário comparando scores reais do Langfuse. Diferente do Coach v2
          (que detecta regressão em treinos sintéticos), este pega drift
          em clientes reais. Mais crítico — aparece PRIMEIRO no banner. */}
      {activeDriftAlert && (
        <DriftAlertBanner agentId={agentId} alert={activeDriftAlert} />
      )}

      {/* Banner de regressão (Coach v2) — aparece só quando o último
          treino detectou piora em relação ao anterior. Objetivo: alerta
          proativo, cliente não precisa abrir o modal de treino pra
          descobrir que algo quebrou. */}
      {regressionInfo && (
        <HealthRegressionAlert
          info={regressionInfo}
          onRetrain={onTrainAgent}
        />
      )}

      {/* Hero: score + interpretação */}
      <HealthScoreHero agentId={agentId} period={period} range={range} />

      {/* Cards narrativos LLM */}
      {loadingInsights && (
        <div
          className="rounded-lg border p-6 text-sm"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
        >
          Gerando análise com IA...
        </div>
      )}

      {!loadingInsights && insights && !insights.hasNarrative && (
        <NarrativeFallback
          reason={insights.fallbackReason}
        />
      )}

      {!loadingInsights && insights && insights.hasNarrative && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InsightCard
            variant="positive"
            title="O que seu agente faz bem"
            items={insights.positive}
            emptyMessage="Assim que houver mais conversas, vamos destacar aqui os pontos fortes do agente."
          />
          <InsightCard
            variant="improvement"
            title="Onde ele pode melhorar"
            items={insights.improvements}
            emptyMessage="Nenhuma oportunidade de melhoria destacada neste período."
          />
          <InsightCard
            variant="insight"
            title="Comportamentos detectados"
            items={insights.insights}
            emptyMessage="Nenhum comportamento específico destacado neste período."
          />
        </div>
      )}

      {/* Gráfico de evolução temporal (sempre 30d) */}
      <HealthTimelineChart agentId={agentId} period="30d" />

      {/* CTAs de próximos passos — abrem AgentTrainModal (UI-2) e tab Lacunas (UI-3) */}
      <HealthRecommendationsPanel
        onTrainAgent={onTrainAgent}
        onViewGaps={onViewGaps}
      />
    </div>
  );
};

interface NarrativeFallbackProps {
  reason?: 'no_openai' | 'llm_error' | 'no_data' | 'parse_error';
}

/**
 * Card de fallback quando o backend não consegue gerar narrativa LLM.
 * Cada motivo tem uma mensagem diferente para orientar o cliente.
 */
const NarrativeFallback: React.FC<NarrativeFallbackProps> = ({ reason }) => {
  const config = getFallbackConfig(reason);
  return (
    <div
      className="rounded-lg border p-5"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div
        className="text-base font-semibold mb-2"
        style={{ color: 'var(--theme-card-text)' }}
      >
        {config.title}
      </div>
      <p
        className="text-sm leading-relaxed"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {config.description}
      </p>
    </div>
  );
};

/**
 * Banner de alerta de drift em PRODUÇÃO — Opção B.
 *
 * Diferente do `HealthRegressionAlert` (que compara treinos sintéticos),
 * este banner alerta sobre drift em clientes REAIS detectado pelo cron
 * diário. Puxa scores do Langfuse dos últimos 7 dias vs anteriores.
 *
 * Cores: vermelho intenso pra `danger` (≥2 métricas regredidas), amarelo
 * pra `warning` (1 métrica regredida).
 *
 * Tem 2 CTAs:
 * - "Entendi" — acknowledge (remove o banner)
 * - Link implícito pra aba "Lacunas" via próprio texto (não bloqueia UX)
 */
const DriftAlertBanner: React.FC<{
  agentId: string;
  alert: AgentDriftAlertResponse;
}> = ({ agentId, alert }) => {
  const acknowledgeMutation = useAcknowledgeDriftAlert();

  const overallDeltaPct = Math.abs(
    (alert.currentOverallScore - alert.previousOverallScore) * 100,
  ).toFixed(1);
  const periodText = `${new Date(alert.currentPeriodStart).toLocaleDateString('pt-BR')} a ${new Date(
    alert.currentPeriodEnd,
  ).toLocaleDateString('pt-BR')}`;

  const isDanger = alert.severity === 'danger';
  const accentColor = isDanger ? '#dc2626' : '#f59e0b';
  const bgColor = isDanger
    ? 'rgba(239, 68, 68, 0.05)'
    : 'rgba(245, 158, 11, 0.05)';
  const borderColor = isDanger
    ? 'rgba(239, 68, 68, 0.25)'
    : 'rgba(245, 158, 11, 0.25)';
  const iconBgColor = isDanger
    ? 'rgba(239, 68, 68, 0.1)'
    : 'rgba(245, 158, 11, 0.1)';

  const handleAcknowledge = (): void => {
    void acknowledgeMutation.mutateAsync({ agentId, alertId: alert.id });
  };

  return (
    <div
      className="rounded-lg border p-5"
      style={{ backgroundColor: bgColor, borderColor }}
    >
      <div className="flex items-start gap-4">
        <div
          className="rounded-full p-2.5 flex-shrink-0"
          style={{ backgroundColor: iconBgColor }}
        >
          <FiActivity className="h-5 w-5" style={{ color: accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className="text-base font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Drift detectado em produção
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-md font-semibold"
              style={{
                backgroundColor: isDanger
                  ? 'rgba(239, 68, 68, 0.15)'
                  : 'rgba(245, 158, 11, 0.15)',
                color: accentColor,
              }}
            >
              {isDanger ? 'Crítico' : 'Atenção'}
            </span>
          </div>
          <p
            className="text-sm leading-relaxed mb-3"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Comparando conversas reais dos últimos 7 dias ({periodText}) com
            os 7 dias anteriores, detectamos{' '}
            <strong style={{ color: accentColor }}>
              queda de {overallDeltaPct}%
            </strong>{' '}
            no score geral do agente. Isso pode indicar que algo mudou no
            padrão dos clientes (novos tipos de pergunta, sazonalidade) ou
            que o agente está tendo dificuldade com casos específicos.
          </p>

          {alert.regressedMetrics.length > 0 && (
            <div className="mb-3">
              <div
                className="text-xs font-semibold mb-1.5"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Métricas afetadas:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {alert.regressedMetrics.map((ev) => (
                  <span
                    key={ev.metric}
                    title={getMetricDescription(ev.metric) || undefined}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium cursor-help"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      color: '#991b1b',
                    }}
                  >
                    {getMetricLabel(ev.metric)}:{' '}
                    {(ev.from * 100).toFixed(0)}% →{' '}
                    <strong>{(ev.to * 100).toFixed(0)}%</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            className="text-xs italic mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Amostra: {alert.currentSampleSize} avaliações no período atual vs{' '}
            {alert.previousSampleSize} no anterior.
          </div>

          <button
            type="button"
            onClick={handleAcknowledge}
            disabled={acknowledgeMutation.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'var(--theme-card-text)',
            }}
          >
            {acknowledgeMutation.isPending && (
              <FiLoader className="h-3 w-3 animate-spin" />
            )}
            Entendi, marcar como revisado
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Banner de alerta proativo — Coach v2 Opção A.
 *
 * Aparece só quando o último treino detectou regressão em relação ao
 * anterior. Dá ao cliente a chance de descobrir problemas imediatamente
 * (ao abrir a tela de Saúde) em vez de ter que rodar um novo treino pra
 * perceber que algo quebrou.
 *
 * Design: vermelho suave (não agressivo), mostra os evaluators que caíram
 * e oferece CTA "Treinar novamente" que abre o modal de treino.
 */
const HealthRegressionAlert: React.FC<{
  info: SnapshotRegressionInfo;
  onRetrain: () => void;
}> = ({ info, onRetrain }) => {
  const overallDeltaPct = Math.abs(info.overallScoreDelta * 100).toFixed(1);
  const previousDate = new Date(info.previousSnapshotCreatedAt).toLocaleString(
    'pt-BR',
    { dateStyle: 'short', timeStyle: 'short' },
  );

  return (
    <div
      className="rounded-lg border p-5"
      style={{
        backgroundColor: 'rgba(239, 68, 68, 0.04)',
        borderColor: 'rgba(239, 68, 68, 0.25)',
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="rounded-full p-2.5 flex-shrink-0"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
        >
          <FiTrendingDown className="h-5 w-5" style={{ color: '#dc2626' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-base font-semibold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Atenção: o último treino regrediu
          </div>
          <p
            className="text-sm leading-relaxed mb-3"
            style={{ color: 'var(--theme-card-text)' }}
          >
            {info.overallScoreDelta < 0 ? (
              <>
                Comparado ao treino anterior ({previousDate}), o score geral
                caiu{' '}
                <strong style={{ color: '#dc2626' }}>
                  −{overallDeltaPct}%
                </strong>
                . Os testes detectam variações no comportamento do agente —
                mesmo sem você ter aplicado mudanças no prompt, condições
                externas (modelo, base de conhecimento, dados de cenário) ou
                a própria natureza estocástica do LLM podem afetar o
                resultado. Vale revisar as métricas regredidas abaixo.
              </>
            ) : (
              <>
                Comparado ao treino anterior ({previousDate}), algumas
                métricas específicas regrediram mesmo com score geral
                estável. Vale revisar quais e se o impacto é aceitável.
              </>
            )}
          </p>
          {info.regressedEvaluators.length > 0 && (
            <div className="space-y-2 mb-4">
              {info.regressedEvaluators.map((ev) => {
                const description = getMetricDescription(ev.metric);
                return (
                  <div
                    key={ev.metric}
                    className="rounded-md px-3 py-2"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                      border: '1px solid rgba(239, 68, 68, 0.15)',
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className="text-sm font-medium"
                        style={{ color: '#991b1b' }}
                      >
                        {getMetricLabel(ev.metric)}
                      </span>
                      <span className="text-xs font-medium" style={{ color: '#991b1b' }}>
                        {(ev.from * 100).toFixed(0)}% →{' '}
                        <strong>{(ev.to * 100).toFixed(0)}%</strong>
                      </span>
                    </div>
                    {description && (
                      <p
                        className="text-xs mt-1 leading-snug"
                        style={{ color: '#7f1d1d' }}
                      >
                        {description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={onRetrain}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              backgroundColor: '#dc2626',
              color: '#ffffff',
            }}
          >
            Revisar e corrigir
          </button>
        </div>
      </div>
    </div>
  );
};

function getFallbackConfig(
  reason: NarrativeFallbackProps['reason'],
): { title: string; description: string } {
  switch (reason) {
    case 'no_data':
      return {
        title: 'Ainda não há dados suficientes',
        description:
          'Execute algumas conversas com este agente para começarmos a gerar análises e sugestões.',
      };
    case 'no_openai':
      return {
        title: 'Integração OpenAI não configurada',
        description:
          'Para gerar análises automáticas, configure a integração OpenAI nas configurações do sistema.',
      };
    case 'llm_error':
      return {
        title: 'Não foi possível gerar análise agora',
        description:
          'Tivemos um problema ao analisar os dados. Tente novamente em alguns minutos.',
      };
    case 'parse_error':
    default:
      return {
        title: 'Análise indisponível no momento',
        description:
          'Não conseguimos processar a resposta da análise. Tente novamente em alguns minutos.',
      };
  }
}
